import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSearch,
    faArrowDownShortWide,
    faArrowDownWideShort,
    faChevronLeft,
    faChevronRight,
    faCircleCheck,
} from "@fortawesome/pro-regular-svg-icons";

import { getLogTypeChipStyleVars } from "../../helpers/StatusMapper";
import InputField from "../InputField";
import Dropdown from "../Dropdown";
import ConnectorStatusBadge from "../connector/ConnectorStatusBadge";
import LoadingSpinner from "../../components/LoadingSpinner";

import {
    parseUtcDate,
    formatSwedishTime,
    formatSwedishDate,
    formatSwedishClock,
} from "../../helpers/DateTime";

const LOG_LEVEL_POINT = "Charging point";
const LOG_LEVEL_CONNECTOR = "Charging connector";

const safeString = (v: any) => (v == null ? "" : String(v));

const normalizeInfo = (v: any) => {
    if (v == null) return "";
    if (typeof v === "string") return v;
    try {
        return JSON.stringify(v);
    } catch {
        return String(v);
    }
};

const parseJsonDeep = (value: any) => {
    if (value == null || value === "") {
        return null;
    }
    if (typeof value === "object") {
        return value;
    }
    if (typeof value !== "string") {
        return null;
    }

    let current = value.trim();
    for (let i = 0; i < 3; i += 1) {
        if (!current.startsWith("{") && !current.startsWith("[")) {
            return null;
        }
        try {
            const parsed = JSON.parse(current);
            if (typeof parsed === "string") {
                current = parsed.trim();
                continue;
            }
            return parsed;
        } catch {
            return null;
        }
    }
    return null;
};

const formatPayloadText = (value: any) => {
    if (value == null || value === "") {
        return null;
    }

    const parsed = parseJsonDeep(value);
    if (parsed != null) {
        try {
            return JSON.stringify(parsed, null, 2);
        } catch {
            return null;
        }
    }

    if (typeof value === "string") {
        const trimmed = value.trim();
        return trimmed || null;
    }

    return safeString(value);
};

const PayloadBlock = ({ value }) => {
    const text = formatPayloadText(value);
    if (!text) {
        return null;
    }

    const isJson = parseJsonDeep(value) != null;

    return (
        <div
            className={`m-0 w-full whitespace-pre-wrap break-words leading-snug ${
                isJson
                    ? "text-xs font-normal"
                    : "text-sm font-normal"
            }`}
        >
            {text}
        </div>
    );
};

const LogInfoCell = ({ info, infoParts }) => {
    if (infoParts) {
        const payloadText = formatPayloadText(infoParts.payload);
        const hasAction = Boolean(infoParts.action);

        if (!hasAction && !payloadText) {
            return null;
        }

        return (
            <div className="flex flex-col gap-1.5">
                {hasAction ? (
                    <span className="text-xs font-bold text-mute-900 break-words">
                        {infoParts.action}
                    </span>
                ) : null}
                {payloadText ? <PayloadBlock value={infoParts.payload} /> : null}
            </div>
        );
    }

    const payloadText = formatPayloadText(info);
    if (!payloadText) {
        return null;
    }

    return <PayloadBlock value={info} />;
};

export const mapHubLogToRow = (item: any) => {
    if (!item) return null;

    const dt = parseUtcDate(item.createdDateTimeUtc);
    if (!dt || isNaN(dt.getTime())) return null;

    const base = {
        id: `${item.logType}-${dt.getTime()}-${Math.floor(Math.random() * 1000)}`,
        dt,
        time: formatSwedishTime(item.createdDateTimeUtc),
        timeDate: formatSwedishDate(item.createdDateTimeUtc),
        timeClock: formatSwedishClock(item.createdDateTimeUtc),
        errorCode: item.errorCode ?? "",
        errorInfo:
            item.errorInfo && item.errorInfo !== "No error to report."
                ? item.errorInfo
                : "",
        chargingConnectorPublicId: item.chargingConnectorPublicId ?? null,
    };

    switch (item.logType) {
        case "ChargingPointStatus":
            return {
                ...base,
                type: LOG_LEVEL_POINT,
                log: "Status",
                name: item.chargingPointName ?? "",
                status: item.status ?? "",
                info: item.vendorErrorCode ?? "",
            };

        case "ChargingConnectorStatus":
            return {
                ...base,
                type: LOG_LEVEL_CONNECTOR,
                log: "Status",
                name: item.chargingConnectorName ?? "",
                status: item.status ?? "",
                info: item.vendorErrorCode ?? "",
            };

        case "NotifyEvent":
            return {
                ...base,
                type: item.chargingConnectorPublicId ? LOG_LEVEL_CONNECTOR : LOG_LEVEL_POINT,
                log: "Status",
                name: item.chargingConnectorName || item.chargingPointName || "",
                status: item.status ?? "",
                info: item.vendorErrorCode || item.reason || "",
            };

        case "Authorization":
            return {
                ...base,
                type: LOG_LEVEL_POINT,
                log: "Authorization",
                name: item.chargingPointName ?? "",
                status: item.status ?? item.authorizationStatus ?? "",
                errorInfo: item.reason ?? item.reason ?? "",
                info:
                    safeString(item.authorizationSource) +
                    (item.tagToken ? `: ${item.tagToken}` : ""),
            };

        case "TransactionStatus": {
            const transactionJson = parseJsonDeep(item.transactionData);
            let info = "";

            if (transactionJson != null) {
                info = transactionJson;
            } else if (item.transactionData) {
                info = item.transactionData;
            } else {
                const synthesized = {};
                if (item.eventType) {
                    synthesized.eventType = item.eventType;
                }
                if (item.chargingState) {
                    synthesized.chargingState = item.chargingState;
                }
                if (item.triggerReason) {
                    synthesized.triggerReason = item.triggerReason;
                }
                if (item.tagToken) {
                    synthesized.idTag = item.tagToken;
                }
                if (item.meterStart != null && item.meterStart !== "") {
                    synthesized.meterStart = item.meterStart;
                }
                if (item.meterStop != null && item.meterStop !== "") {
                    synthesized.meterStop = item.meterStop;
                }
                if (Object.keys(synthesized).length > 0) {
                    info = synthesized;
                }
            }

            const chargingState =
                (typeof info === "object" && info?.chargingState) ||
                item.chargingState ||
                "";
            const eventType =
                (typeof info === "object" && info?.eventType) ||
                item.eventType ||
                "";
            const isEnded = String(eventType).toLowerCase() === "ended";

            return {
                ...base,
                type: LOG_LEVEL_CONNECTOR,
                log: "Transaction",
                name: item.chargingConnectorName ?? "",
                status: (!isEnded && chargingState) || item.status || "",
                info,
            };
        }

        case "BootNotification":
            return {
                ...base,
                type: LOG_LEVEL_POINT,
                log: "Bootnotification",
                name: item.chargingPointName ?? "",
                status: item.status ?? item.registrationStatus ?? "",
                info: item.firmwareVersion ?? "",
            };

        case "Heartbeat":
            return {
                ...base,
                type: LOG_LEVEL_POINT,
                log: "Heartbeat",
                name: item.chargingPointName ?? "",
                status: item.status ?? "Accepted",
                info: "",
            };

        case "ChargingCommand":
            return {
                ...base,
                type: LOG_LEVEL_CONNECTOR,
                log: "Charging-Command",
                name: item.chargingConnectorName ?? "",
                status: item.status ?? "",
                errorInfo: item.reason ?? "",
                info: [item.commandType, item.remotePartyName]
                    .filter(Boolean)
                    .join(": "),
            };

        case "ReservationStatus":
            return {
                ...base,
                type:
                    item.level === "ChargingConnector"
                        ? LOG_LEVEL_CONNECTOR
                        : LOG_LEVEL_POINT,
                log: "Reservation",
                name:
                    item.chargingConnectorName ??
                    item.chargingPointName ??
                    "",
                status: item.status ?? "",
                info: normalizeInfo(item.reservationData),
            };

        case "FirmwareLog":
            return {
                ...base,
                type: LOG_LEVEL_POINT,
                log: "Firmware",
                name: item.chargingPointName ?? "",
                status: item.status ?? "",
                info: item.info ?? item.firmwareVersion ?? "",
            };

        case "ChangeRequest":
            return {
                ...base,
                type:
                    item.level === "ChargingConnector"
                        ? LOG_LEVEL_CONNECTOR
                        : LOG_LEVEL_POINT,
                log: "Change-Request",
                name:
                    item.chargingConnectorName ??
                    item.chargingPointName ??
                    "",
                status: item.status ?? "",
                info: "",
                infoParts: {
                    action: item.commandType ?? null,
                    payload: item.transactionData,
                },
            };

        case "BookingRequest":
            return {
                ...base,
                type: LOG_LEVEL_CONNECTOR,
                log: "Booking-Request",
                name: item.chargingConnectorName ?? "",
                status: item.status ?? "",
                errorInfo: item.reason ?? "",
                info: item.remotePartyName ?? "",
            };

        case "ChargeAttempt":
            return {
                ...base,
                type: LOG_LEVEL_CONNECTOR,
                log: "Charge-Attempt",
                name: item.chargingConnectorName ?? "",
                status: item.status ?? "",
                errorInfo: item.reason ?? "",
                info:
                    safeString(item.commandType) +
                    (item.tagToken ? `: ${item.tagToken}` : ""),
            };

        default:
            return {
                ...base,
                type:
                    item.level === "ChargingConnector"
                        ? LOG_LEVEL_CONNECTOR
                        : item.level === "ChargingPoint"
                            ? LOG_LEVEL_POINT
                            : safeString(item.level),
                log: safeString(item.logType),
                name:
                    item.chargingConnectorName ??
                    item.chargingPointName ??
                    "",
                status: item.status ?? "",
                info: item.vendorErrorCode ?? item.remotePartyName ?? "",
            };
    }
};

const LogTable = ({
    rows,
    loading,
    sessionTime = null,
    pagination = null,
    allLogTypes = [],
    selectedLogTypes = [],
    onToggleLogType,
    onPageChange,
    onPageSizeChange,
    search = "",
    onSearchChange,
    minSearchLength = 3,
    showSearchRangeWarning = false,
}) => {
    const { t } = useTranslation();
    const [sortColumn, setSortColumn] = useState("dt");
    const [sortOrder, setSortOrder] = useState("desc");

    const showLoadingOverlay = loading;

    const sessionFormattedTime = sessionTime
        ? formatSwedishTime(sessionTime)
        : "";

    const normalizedSearch = search.trim();
    const showSearchHint =
        normalizedSearch.length > 0 &&
        normalizedSearch.length < minSearchLength;

    const columnLayout = useMemo(
        () => ({
            time: "min-w-[8.5rem]",
            log: "min-w-[5rem]",
            type: "min-w-[8.5rem]",
            name: "min-w-[8rem]",
            status: "min-w-[6rem]",
            info: "min-w-[16rem]",
            errorInfo: "min-w-[9rem]",
            errorCode: "min-w-[7.5rem]",
        }),
        []
    );

    const columns = useMemo(
        () => [
            { label: t("common.time"), key: "time" },
            { label: t("logs.log", { defaultValue: "Log" }), key: "log" },
            { label: t("common.type"), key: "type" },
            { label: t("common.name"), key: "name" },
            { label: t("common.status"), key: "status" },
            { label: t("common.info"), key: "info" },
            { label: t("logs.errorInfo"), key: "errorInfo" },
            { label: t("logs.errorCode"), key: "errorCode" },
        ],
        [t]
    );

    const columnCount = columns.length;

    const logTypes = useMemo(() => allLogTypes, [allLogTypes]);

    const visibleRows = useMemo(() => {
        const r = [...(rows ?? [])];

        r.sort((a, b) => {
            if (sortColumn === "dt") {
                return sortOrder === "asc" ? a.dt - b.dt : b.dt - a.dt;
            }

            const av = safeString(a[sortColumn]);
            const bv = safeString(b[sortColumn]);

            return (
                av.localeCompare(bv, undefined, { sensitivity: "base" }) *
                (sortOrder === "asc" ? 1 : -1)
            );
        });

        return r;
    }, [rows, sortColumn, sortOrder]);

    const total = pagination?.totalCount ?? visibleRows.length;
    const pages = pagination?.totalPages ?? 1;
    const currentPage = pagination?.page ?? 1;
    const currentPageSize = pagination?.pageSize ?? 100;

    return (
        <div className="flex flex-col flex-1">
            <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
                <div className="flex flex-wrap gap-2">
                    {logTypes.map((logType) => {
                        const active = selectedLogTypes.includes(logType);

                        return (
                            <button
                                key={logType}
                                onClick={() => onToggleLogType?.(logType)}
                                className={`chip ${active ? "chip--active" : ""} shadow-md`}
                                style={getLogTypeChipStyleVars(logType)}
                                type="button"
                            >
                                <FontAwesomeIcon
                                    icon={faCircleCheck}
                                    className="chip__check"
                                />
                                {t(`logType.${logType}`, { defaultValue: logType })}
                            </button>
                        );
                    })}
                </div>

                <div className="flex gap-2 items-start">
                    <div className="w-44">
                        <InputField
                            icon={faSearch}
                            placeholder={t("common.search")}
                            value={search}
                            onChange={(e) => onSearchChange?.(e.target.value)}
                        />
                        {showSearchHint && (
                            <p className="text-xs text-mute-500 mt-1">
                                {t("logs.minSearch", { count: minSearchLength })}
                            </p>
                        )}
                        {showSearchRangeWarning && (
                            <p className="mt-1 text-xs text-warning-700">
                                {t("logs.searchRangeWarning")}
                            </p>
                        )}
                    </div>

                </div>
            </div>

            <div className="relative">
                <div className="border rounded-md overflow-x-auto">
                    <table className="table-default w-full min-w-[69.5rem] text-sm">
                        <thead>
                            <tr>
                                <th
                                    className={`px-3 py-2 cursor-pointer ${columnLayout.time}`}
                                    onClick={() => {
                                        setSortColumn("dt");
                                        setSortOrder((p) =>
                                            p === "asc" ? "desc" : "asc"
                                        );
                                    }}
                                >
                                    <div className="flex items-center gap-1">
                                        {t("common.time")}
                                        {sortColumn === "dt" && (
                                            <FontAwesomeIcon
                                                icon={
                                                    sortOrder === "asc"
                                                        ? faArrowDownShortWide
                                                        : faArrowDownWideShort
                                                }
                                                className="w-3"
                                            />
                                        )}
                                    </div>
                                </th>

                                {columns
                                    .filter((c) => c.key !== "time")
                                    .map((col) => (
                                        <th
                                            key={col.key}
                                            className={`px-3 py-2 cursor-pointer ${columnLayout[col.key] ?? ""}`}
                                            onClick={() => {
                                                setSortColumn(col.key);
                                                setSortOrder((p) =>
                                                    p === "asc" ? "desc" : "asc"
                                                );
                                            }}
                                        >
                                            <div className="flex items-center gap-1">
                                                {col.label}
                                                {sortColumn === col.key && (
                                                    <FontAwesomeIcon
                                                        icon={
                                                            sortOrder === "asc"
                                                                ? faArrowDownShortWide
                                                                : faArrowDownWideShort
                                                        }
                                                        className="w-3"
                                                    />
                                                )}
                                            </div>
                                        </th>
                                    ))}
                            </tr>
                        </thead>

                        <tbody>
                            {showLoadingOverlay ? (
                                <tr>
                                    <td colSpan={columnCount} className="px-3 py-10 text-center">
                                        <div className="flex justify-center">
                                            <LoadingSpinner />
                                        </div>
                                    </td>
                                </tr>
                            ) : visibleRows.length > 0 ? (
                                visibleRows.map((log) => (
                                    <tr
                                        key={log.id}
                                        className={`px-3 py-2 ${sessionFormattedTime === log.time
                                                ? "bg-mute-100 font-semibold"
                                                : ""
                                            }`}
                                    >
                                        <td className={`px-3 py-2 ${columnLayout.time}`}>
                                            <div className="flex flex-col leading-tight">
                                                <span>{log.timeDate}</span>
                                                <span >
                                                    {log.timeClock}
                                                </span>
                                            </div>
                                        </td>

                                        <td
                                            className={`px-3 py-2 break-words whitespace-normal ${columnLayout.log}`}
                                        >
                                            {t(`logType.${log.log}`, { defaultValue: log.log })}
                                        </td>

                                        <td
                                            className={`px-3 py-2 break-words whitespace-normal ${columnLayout.type}`}
                                        >
                                            {t(`logs.level.${log.type}`, { defaultValue: log.type })}
                                        </td>

                                        <td
                                            className={`px-3 py-2 break-words whitespace-normal ${columnLayout.name}`}
                                        >
                                            {log.name}
                                        </td>

                                        <td className={`px-3 py-2 ${columnLayout.status}`}>
                                            <ConnectorStatusBadge status={log.status} />
                                        </td>

                                        <td className={`px-3 py-2 align-middle ${columnLayout.info}`}>
                                            <LogInfoCell
                                                info={log.info}
                                                infoParts={log.infoParts}
                                            />
                                        </td>

                                        <td
                                            className={`px-3 py-2 break-words whitespace-normal ${columnLayout.errorInfo}`}
                                        >
                                            {log.errorInfo &&
                                            log.errorInfo !== "No error to report."
                                                ? t(`logs.reason.${log.errorInfo}`, { defaultValue: log.errorInfo })
                                                : ""}
                                        </td>

                                        <td
                                            className={`px-3 py-2 break-words whitespace-normal ${columnLayout.errorCode}`}
                                        >
                                            {log.errorCode}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={columnCount} className="px-3 py-10 text-center text-mute-400">
                                        {t("logs.noLogs")}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="flex justify-end gap-4 pt-4 items-center">
                <p>{t("pagination.rowsPerPage")}</p>
                <div className="w-32">
                    <Dropdown
                        options={["10", "30", "50", "100", "1000"]}
                        selectedOptions={[String(currentPageSize)]}
                        onSelect={(v) => {
                            const next = parseInt(v, 10);
                            onPageSizeChange?.(next);
                        }}
                    />
                </div>

                <p>
                    {total === 0
                        ? t("pagination.range", { start: 0, end: 0, total: 0 })
                        : t("pagination.range", {
                            start: (currentPage - 1) * currentPageSize + 1,
                            end: Math.min(currentPage * currentPageSize, total),
                            total,
                        })}
                </p>

                <FontAwesomeIcon
                    icon={faChevronLeft}
                    className={`cursor-pointer ${currentPage <= 1
                        ? "opacity-40 pointer-events-none"
                        : ""
                        }`}
                    onClick={() => {
                        if (currentPage > 1) {
                            onPageChange?.(currentPage - 1);
                        }
                    }}
                />
                <FontAwesomeIcon
                    icon={faChevronRight}
                    className={`cursor-pointer ${currentPage >= pages
                        ? "opacity-40 pointer-events-none"
                        : ""
                        }`}
                    onClick={() => {
                        if (currentPage < pages) {
                            onPageChange?.(currentPage + 1);
                        }
                    }}
                />
            </div>
        </div>
    );
};

export default LogTable;
